import { cva } from 'class-variance-authority';

export enum CardType {
    Card = 'card',
    Tile = 'tile',
    CardAdvanced = 'cardAdvanced',
}

export enum CardSize {
    S = 's',
    M = 'm',
    L = 'l',
}

export enum CardDirection {
    Col = 'col',
    Row = 'row',
    TabletCol = 'tabletCol',
    TabletRow = 'tabletRow',
    DesktopSCol = 'desktopSCol',
    DesktopSRow = 'desktopSRow',
}

export enum CardBackground {
    Primary = 'primary',
    Neutral = 'neutral',
}

export enum CardBorder {
    Default = 'default',
    None = 'none',
    Error = 'error',
    Selected = 'selected',
}

const base = 'flex flex-1 rounded-0.5 overflow-hidden';

export type CardClassNames = {
    width?: `w-[${number}rem]` | `max-w-[${number}rem]` | `w-${string}` | `max-w-${string}`;
    height?: `h-[${number}rem]` | `max-h-[${number}rem]` | `h-${string}` | `min-${string}`;
    align?: 'items-center' | 'items-start' | 'items-end';
};

const direction = cva('', {
    variants: {
        direction: {
            [CardDirection.Col]: 'flex-col',
            [CardDirection.Row]: 'flex-row',
            [CardDirection.TabletRow]: 'flex-col tablet:flex-row',
            [CardDirection.TabletCol]: 'flex-row tablet:flex-col',
            [CardDirection.DesktopSRow]: 'flex-col desktopS:flex-row',
            [CardDirection.DesktopSCol]: 'flex-row desktopS:flex-col',
        },
    },
    defaultVariants: {
        direction: CardDirection.Row,
    },
});

const padding = cva('', {
    variants: {
        size: {
            [CardSize.S]: 'p-0.5',
            [CardSize.M]: 'p-1',
            [CardSize.L]: 'p-1.5',
        },
    },
    defaultVariants: {
        size: CardSize.M,
    },
});

const border = cva('', {
    variants: {
        border: {
            [CardBorder.Default]:
                'outline outline-border-primary outline-medium focus:[outline-style:auto] focus:outline-border-info',
            [CardBorder.Error]:
                'outline outline-border-error outline-bold focus:[outline-style:auto] focus:outline-border-info',
            [CardBorder.None]: '',
            [CardBorder.Selected]:
                'outline outline-border-selected outline-bold focus:[outline-style:auto] focus:outline-border-info',
        },
    },
    defaultVariants: {
        border: CardBorder.Default,
    },
});

const getBorderClass = (props: { border?: CardBorder; selected?: boolean }) => {
    return border({ border: props.selected ? CardBorder.Selected : props.border });
};

const gap = cva('', {
    variants: {
        size: {
            [CardSize.S]: 'gap-0.5',
            [CardSize.M]: 'gap-1',
            [CardSize.L]: 'gap-1.5',
        },
    },
    defaultVariants: {
        size: CardSize.M,
    },
});

const paddingWithImage = {
    [CardSize.S]: {
        [CardDirection.Col]: 'pt-0',
        [CardDirection.Row]: 'pl-0',
        [CardDirection.TabletCol]: 'pl-0 tablet:pt-0 tablet:pl-0.5',
        [CardDirection.TabletRow]: 'pt-0 tablet:pl-0 tablet:pt-0.5',
        [CardDirection.DesktopSCol]: 'pl-0 desktopS:pt-0 desktopS:pl-0.5',
        [CardDirection.DesktopSRow]: 'pt-0 desktopS:pl-0 desktopS:pt-0.5',
    },
    [CardSize.M]: {
        [CardDirection.Col]: 'pt-0',
        [CardDirection.Row]: 'pl-0',
        [CardDirection.TabletCol]: 'pl-0 tablet:pt-0 tablet:pl-1',
        [CardDirection.TabletRow]: 'pt-0 tablet:pl-0 tablet:pt-1',
        [CardDirection.DesktopSCol]: 'pl-0 desktopS:pt-0 desktopS:pl-1',
        [CardDirection.DesktopSRow]: 'pt-0 desktopS:pl-0 desktopS:pt-1',
    },
    [CardSize.L]: {
        [CardDirection.Col]: 'pt-0',
        [CardDirection.Row]: 'pl-0',
        [CardDirection.TabletCol]: 'pl-0 tablet:pt-0 tablet:pl-1.5',
        [CardDirection.TabletRow]: 'pt-0 tablet:pl-0 tablet:pt-1.5',
        [CardDirection.DesktopSCol]: 'pl-0 desktopS:pt-0 desktopS:pl-1.5',
        [CardDirection.DesktopSRow]: 'pt-0 desktopS:pl-0 desktopS:pt-1.5',
    },
};

const backgroundCard = cva('', {
    variants: {
        background: {
            [CardBackground.Primary]: 'bg-background-l0',
            [CardBackground.Neutral]: 'bg-background-neutral',
        },
    },
    defaultVariants: {
        background: CardBackground.Primary,
    },
});

const backgroundTile = cva('cursor-pointer disabled:bg-background-disabled', {
    variants: {
        background: {
            [CardBackground.Primary]:
                'bg-surface-primary hover:bg-surface-primary-hover focus:bg-surface-primary active:bg-surface-primary-pressed',
            [CardBackground.Neutral]:
                'bg-surface-neutral hover:bg-surface-neutral-hover focus:bg-surface-neutral active:bg-surface-neutral-pressed',
        },
    },
    defaultVariants: {
        background: CardBackground.Primary,
    },
});

const isDisabled = 'bg-background-disabled cursor-not-allowed';

export const CardStyle = {
    base,
    gap,
    padding,
    paddingWithImage,
    direction,
    isDisabled,
    getBorderClass,
    backgroundCard,
    backgroundTile,
};
